'use client';

import { useState, useEffect, useRef } from 'react';

interface StatsSectionProps {
  currentLang?: 'sl' | 'en';
}

export default function StatsSection({ currentLang = 'sl' }: StatsSectionProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [counts, setCounts] = useState({ years: 0, customers: 0, hives: 0, products: 0 });
  const sectionRef = useRef<HTMLDivElement>(null);

  const stats = [
    {
      id: 'stat_years',
      target: 20,
      label: currentLang === 'sl' ? 'let izkušenj' : 'years experience',
      suffix: '+',
    },
    {
      id: 'stat_customers',
      target: 500,
      label: currentLang === 'sl' ? 'zadovoljnih strank' : 'satisfied customers',
      suffix: '+',
    },
    {
      id: 'stat_hives',
      target: 200,
      label: currentLang === 'sl' ? 'aktivnih panjev' : 'active hives',
      suffix: '+',
    },
    {
      id: 'stat_products',
      target: 10,
      label: currentLang === 'sl' ? 'vrst produktov' : 'product types',
      suffix: '+',
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    const duration = 2000;
    const steps = 60;
    const stepDuration = duration / steps;

    let currentStep = 0;
    const interval = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      const easing = 1 - Math.pow(1 - progress, 3); // Ease out cubic

      setCounts({
        years: Math.floor(stats[0].target * easing),
        customers: Math.floor(stats[1].target * easing),
        hives: Math.floor(stats[2].target * easing),
        products: Math.floor(stats[3].target * easing),
      });

      if (currentStep >= steps) {
        clearInterval(interval);
        setCounts({
          years: stats[0].target,
          customers: stats[1].target,
          hives: stats[2].target,
          products: stats[3].target,
        });
      }
    }, stepDuration);

    return () => clearInterval(interval);
  }, [isVisible]);

  return (
    <section
      ref={sectionRef}
      className="py-20 px-6 md:px-10 bg-accent/20"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-12">
          {stats.map((stat, index) => (
            <div key={stat.id} className="text-center">
              <div className="text-5xl md:text-6xl font-bold text-primary mb-3">
                {Object.values(counts)[index]}{stat.suffix}
              </div>
              <p className="text-sm uppercase tracking-widest text-muted-foreground">
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}